<template>
  <div v-if="show" class="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center">
    <div class="bg-white p-6 rounded shadow-md w-full max-w-lg">
      <h2 class="text-xl font-bold mb-4">Complete Your Profile</h2>
      <form @submit.prevent="submitProfile">
        
        <!-- Name -->
        <div class="mb-4">
          <label class="block mb-1 text-sm">Name</label>
          <input v-model="form.name" required class="w-full border px-3 py-2 rounded" />
        </div>

        <!-- Institution -->
        <div class="mb-4">
          <label class="block mb-1 text-sm">Institution</label>
          <input v-model="form.institution" required class="w-full border px-3 py-2 rounded" />
        </div>

        <!-- Field of Studies -->
        <div class="mb-4">
          <label class="block mb-1 text-sm">Field of Studies</label>
          <input v-model="form.fieldOfStudies" required class="w-full border px-3 py-2 rounded" />
        </div>

        <!-- ORCID -->
        <div class="mb-4">
          <label class="block mb-1 text-sm">ORCID</label>
          <input 
            v-model="form.orcid"
            class="w-full border px-3 py-2 rounded"
            placeholder="0000-0000-0000-0000"
            @blur="validateOrcid"
          />
          <p v-if="errors.orcid" class="text-red-500 text-xs mt-1">{{ errors.orcid }}</p>
        </div>

        <!-- Google Scholar ID -->
        <div class="mb-4">
          <label class="block mb-1 text-sm">Google Scholar ID</label>
          <input 
            v-model="form.googleScholarId"
            class="w-full border px-3 py-2 rounded"
            placeholder="e.g., VjJ2vSAAAAAJ"
            @blur="validateGoogleScholarId"
          />
          <p v-if="errors.googleScholarId" class="text-red-500 text-xs mt-1">{{ errors.googleScholarId }}</p>
        </div>

        <!-- Scopus ID -->
        <div class="mb-4">
          <label class="block mb-1 text-sm">Scopus Author ID</label>
          <input 
            v-model="form.scopusId"
            class="w-full border px-3 py-2 rounded"
            placeholder="e.g., 57212345678"
            @blur="validateScopusId"
          />
          <p v-if="errors.scopusId" class="text-red-500 text-xs mt-1">{{ errors.scopusId }}</p>
        </div>

        <!-- Hidden role -->
        <div class="mb-4">
          <input type="hidden" :value="role" />
        </div>
     
        <!-- Buttons -->
        <div class="flex justify-end">
          <button type="submit" class="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700">
            Save Profile
          </button>
        </div>
      </form>

      <!-- Sign out -->
      <div class="flex justify-start mt-3">
        <div class="flex-1">
          <a href="#" @click.prevent="$emit('logout')" class="dropdown-item">Sign out</a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'UserProfileModal',
  props: {
    show: Boolean,
    user: Object,
  },
  emits: ['close', 'profile-completed', 'logout'],
  data() {
    return {
      form: {
        name: '',
        institution: '',
        fieldOfStudies: '',
        orcid: '',
        googleScholarId: '',
        scopusId: ''
      },
      role: 'researcher', // Default role
      errors: {
        orcid: '',
        googleScholarId: '',
        scopusId: ''
      }
    };
  },
  async created() {
    if (this.user?.email) {
      this.setInstitutionFromEmail(this.user.email);
      await this.checkInviteRoleByEmail(this.user.email);
    }
  },
  watch: {
    user(newUser) {
      if (newUser) {
        this.form.name = newUser.name || '';
        if (newUser.email) {
          this.setInstitutionFromEmail(newUser.email);
          this.checkInviteRoleByEmail(newUser.email);
        }
      }
    },
    show(newVal) {
      if (newVal && this.user?.email) {
        this.setInstitutionFromEmail(this.user.email);
        this.checkInviteRoleByEmail(this.user.email);
      }
    }
  },
  methods: {
    // ✅ Extract institution from .edu.ph email
    setInstitutionFromEmail(email) {
      if (email.endsWith('.edu.ph')) {
        const domain = email.split('@')[1];
        this.form.institution = domain.replace('.edu.ph', '').toUpperCase();
      }
    },
    async checkInviteRoleByEmail(email) {
      try {
        const { getFirestore, collection, query, where, getDocs } = await import('firebase/firestore');
        const db = getFirestore();
        const invitationsRef = collection(db, 'invitations');
        const q = query(invitationsRef, where('email', '==', email));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          const invitationData = querySnapshot.docs[0].data();
          this.role = invitationData.role || 'researcher';
        } else {
          this.role = 'researcher';
        }
      } catch (err) {
        console.error('Error checking invitation by email:', err);
        this.role = 'researcher';
      }
    },

    // ✅ Validators
    validateOrcid() {
      const regex = /^\d{4}-\d{4}-\d{4}-\d{4}$/;
      this.errors.orcid = this.form.orcid && !regex.test(this.form.orcid)
        ? "Invalid ORCID format (expected 0000-0000-0000-0000)"
        : "";
    },
    validateGoogleScholarId() {
      const regex = /^[a-zA-Z0-9_-]{12}$/; // typical GS IDs are 12 chars
      this.errors.googleScholarId = this.form.googleScholarId && !regex.test(this.form.googleScholarId)
        ? "Invalid Google Scholar ID format"
        : "";
    },
    validateScopusId() {
      const regex = /^\d{8,11}$/; // Scopus IDs are numeric 8-11 digits
      this.errors.scopusId = this.form.scopusId && !regex.test(this.form.scopusId)
        ? "Invalid Scopus Author ID (must be 8–11 digits)"
        : "";
    },

    // ✅ Save profile
    async submitProfile() {
      // run validations
      this.validateOrcid();
      this.validateGoogleScholarId();
      this.validateScopusId();

      if (this.errors.orcid || this.errors.googleScholarId || this.errors.scopusId) {
        alert("Please correct validation errors before saving.");
        return;
      }

      try {
        const { getFirestore, doc, setDoc } = await import('firebase/firestore');
        const db = getFirestore();
        await setDoc(doc(db, 'users', this.user.uid), {
          ...this.form,
          email: this.user.email,
          role: this.role
        }, { merge: true });

        this.$emit('profile-completed');
      } catch (err) {
        console.error('Error saving profile:', err);
        alert('Failed to save profile. Please try again.');
      }
    }
  }
};
</script>
